import type { EntitlementSnapshot, NikolaRoute } from "./entitlements.api";

export type EntitlementCta = "manage_trial" | "join_club" | "add_credits";
export interface ActionEntitlement {
  allowed: boolean;
  estimate: number;
  cta?: EntitlementCta;
  nextGrantAt?: string;
}

export const resolveNikolaAction = (
  snapshot: EntitlementSnapshot | null,
  route: NikolaRoute,
  gameId?: string,
): ActionEntitlement => {
  if (!snapshot) return { allowed: false, estimate: 0 };
  const estimate = snapshot.routeEstimates[route];
  const minimum = route === "chat" ? 1 : estimate;
  if (snapshot.credits.purchased >= minimum) return { allowed: true, estimate };
  if (snapshot.membership.active) {
    return snapshot.credits.club + snapshot.credits.purchased >= minimum
      ? { allowed: true, estimate }
      : {
          allowed: false,
          estimate,
          cta: "add_credits",
          ...(snapshot.membership.nextGrantAt ? { nextGrantAt: snapshot.membership.nextGrantAt } : {}),
        };
  }
  if (!snapshot.onboarding.complete) {
    const inScope = Boolean(gameId && snapshot.onboarding.gameId === gameId);
    if (inScope && snapshot.onboarding.credits.remaining >= minimum) return { allowed: true, estimate };
    return { allowed: false, estimate, cta: "manage_trial" };
  }
  if (!snapshot.trial.eligible) {
    return snapshot.coreOpenBeta && snapshot.credits.onboarding >= minimum
      ? { allowed: true, estimate }
      : { allowed: false, estimate, cta: "join_club" };
  }
  const analyzed = snapshot.trial.selectedGames.filter((game) => game.locked);
  const inScope = Boolean(gameId && analyzed.some((game) => game.gameId === gameId));
  if (!inScope) return { allowed: false, estimate, cta: "manage_trial" };
  return snapshot.trial.credits.remaining >= minimum
    ? { allowed: true, estimate }
    : { allowed: false, estimate, cta: "join_club" };
};

export const resolveAnalysisAction = (
  snapshot: EntitlementSnapshot | null,
  gameId: string,
  alreadyAnalyzed = false,
  exactTimeControl?: string | null,
): Omit<ActionEntitlement, "estimate"> => {
  if (!snapshot) return { allowed: false };
  if (snapshot.coreOpenBeta || snapshot.membership.active) return { allowed: true };
  if (!snapshot.onboarding.complete) {
    const consumed = snapshot.onboarding.analyzedGameCount ?? (snapshot.onboarding.gameId ? 1 : 0);
    return consumed === 0 || (consumed === 1 && snapshot.onboarding.gameId === gameId)
      ? { allowed: true }
      : { allowed: false, cta: "manage_trial" };
  }
  if (!snapshot.trial.eligible) return { allowed: false, cta: "join_club" };
  if (alreadyAnalyzed || snapshot.trial.selectedGames.some((game) => game.gameId === gameId)) {
    return { allowed: true };
  }
  if (!exactTimeControl || (snapshot.trial.exactTimeControl && snapshot.trial.exactTimeControl !== exactTimeControl)) {
    return { allowed: false, cta: "manage_trial" };
  }
  return snapshot.trial.selectedGames.filter((game) => game.locked).length + (snapshot.trial.deletedGameCount ?? 0) <
    snapshot.trial.gameLimit
    ? { allowed: true }
    : { allowed: false, cta: "join_club" };
};
